import { Injectable, inject } from "@angular/core";
import { catchError, map, switchMap, takeWhile, withLatestFrom } from "rxjs";

import { Actions, createEffect, ofType } from "@ngrx/effects";
// Store
import { Store } from "@ngrx/store";
import * as SportsbookActions from "../../../stores/sportsbook/sportsbook.actions";
import { SportsbookFacade } from "../../../stores/sportsbook/sportsbook.facade";
import { SportsbookState } from "../../../stores/sportsbook/sportsbook.reducers";

// Services
import { DeviceService } from "@services/device.service";
import { BufferFacade } from "@shared-providers/facades";
import { SportTrackPollingService } from "@sports-services/index";
import { SPORTS_NAVIGATION_SERVICE_ERROR_CODES } from "@sports-services/sports.navigation.service";
import { SportService } from "@sports-services/sports.service";
import { GoalScorerUtils, SportsbookUtils } from "@sports-utils/index";

// Models
import * as models from "@sports-models/index";

// Utils
import { SportBookService } from "@sports-services/index";
import { SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION } from "@sports-services/sports.navigation.service/sports.navigations.constants";
import { SportsConstants } from "@sports-utils/index";

@Injectable({ providedIn: "root" })
export class SportsbookEventEffects {
  // Services
  actions$ = inject(Actions);
  bufferFacade = inject(BufferFacade);
  sportService = inject(SportService);

  // Utils
  sportBookService = inject(SportBookService);
  deviceService = inject(DeviceService);
  store = inject(Store<SportsbookState>);
  sportsbookFacade = inject(SportsbookFacade);
  sportTrackPollingService = inject(SportTrackPollingService);
  goalScorerUtils = inject(GoalScorerUtils);
  sportsbookUtils = inject(SportsbookUtils);
  isDesktop: boolean;
  isMobile: boolean;

  timer = {
    live: "live",
    regular: "regular",
    highlights: "highlights",
  };

  constructor() {
    this.isDesktop = this.deviceService.isDesktop();
    this.isMobile = this.deviceService.isMobile();
  }

  // EVENT PAGE
  fetchEventGamesNoLive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.fetchEventGamesNoLive),
      switchMap(({ type, ...payload }) => {
        const { market, league } = payload;
        return this.sportService.getGamesNoLiveEvent(market.NodeId).pipe(
          map((markets: any[]) => {
            markets.forEach((market, index) => {
              market.nameLeague = league.SportHandle;
              this.sportBookService.getAllSpovesInMarket(markets, market);
            });
            this.sportBookService.getCombinedMarkets(markets, market);
            markets = this.goalScorerUtils.getMarketGoalScorer(markets, market);
            markets = this.sportsbookUtils.orderThreeColumns(markets);
            return SportsbookActions.setEventGamesNoLive({
              markets,
            });
          }),
          catchError((e) => {
            this.sportsbookFacade.setSportsError({
              errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_ERROR,
            });
            return [];
          }),
        );
      }),
    ),
  );

  fetchEventMainCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.fetchEventMainCategories),
      withLatestFrom(this.store.select("sportsbook")),
      switchMap(([eventPageData, store]) => {
        if (!eventPageData.eventMainCategories) {
          const initialEventMainCategories = this.sportBookService.setEventMainCategories(eventPageData.event);
          eventPageData = {
            ...eventPageData,
            eventMainCategories: initialEventMainCategories,
            selectedEventMainCategory: initialEventMainCategories[0],
          };
        }
        this.sportTrackPollingService.stopAllPollings();
        return this.sportService
          .GetCategoriesByLeague({
            idLeague: eventPageData.event.NodeId,
            sportHandle: eventPageData.event.SportHandle,
            countryCode: eventPageData.event.CountryCode,
          })
          .pipe(
            map((eventMarkesCategories: any) => {
              const hasSixPack = models.SixPack.validate(
                eventPageData.event.SportHandle,
                eventPageData.event.CountryCode,
              );
              eventMarkesCategories = eventMarkesCategories.map((cat) => {
                const categoryName = !cat?.CategoryShortName ? cat.CategoryName : cat.CategoryShortName;
                return new models.C_LeagueCategory(cat.CategoryId, categoryName, cat.IsRelevant);
              });

              const gameTypesStored: models.C_GameTypes = new models.C_GameTypes(store.config.gameTypes);
              const gameTypes = gameTypesStored.getSport(eventPageData.event.SportHandle);

              if (hasSixPack) {
                eventMarkesCategories.unshift(new models.C_LeagueCategory(-1, "Principal", false));
                eventMarkesCategories.filter(
                  (category) => gameTypes.indexOf(parseInt(category.CategoryId)) === -1,
                );
              }
              if (!this.isMobile && !hasSixPack) {
                eventMarkesCategories.unshift(new models.C_LeagueCategory(-1, "Principal", false));
              }

              if (eventMarkesCategories.length <= 0) {
                eventMarkesCategories.unshift(new models.C_LeagueCategory(-1, "Principal", false));
              }
              return SportsbookActions.fetchEventGames({
                eventMainCategories: eventPageData.eventMainCategories,
                eventMarkesCategories: eventMarkesCategories,
                games: [],
                specialMarkets: null,
                leagueName: eventPageData.event.Name,
                event: new models.C_EventPageLeague(eventPageData.event),
                parentId: eventPageData.event.NodeId,
                selectedEventMainCategory: eventPageData.selectedEventMainCategory,
                selectedMarketsCategory: eventMarkesCategories[0],
              });
            }),
            catchError((e) => {
              this.sportsbookFacade.setSportsError({
                errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_ERROR,
                errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_1,
              });
              return [];
            }),
          );
      }),
    ),
  );

  setEventMarketSelectedCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.setEventMarketSelectedCategory),
      withLatestFrom(this.store.select("sportsbook")),
      map(([selectedMarketsCategory, sportsbook]) => {
        const eventPageData = sportsbook["eventData"];
        return SportsbookActions.fetchEventGames({
          eventMainCategories: eventPageData.eventMainCategories,
          eventMarkesCategories: eventPageData.eventMarkesCategories,
          games: eventPageData.games,
          specialMarkets: eventPageData.specialMarkets,
          leagueName: eventPageData.leagueName,
          event: eventPageData.event,
          parentId: eventPageData.parentId,
          selectedEventMainCategory: eventPageData.selectedEventMainCategory,
          selectedMarketsCategory,
        });
      }),
    ),
  );

  setEventMainSelectedCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.setEventMainSelectedCategory),
      withLatestFrom(this.store.select("sportsbook")),
      map(([selectedEventMainCategory, sportsbook]) => {
        const eventPageData = sportsbook["eventData"];
        if (selectedEventMainCategory.name === SportsConstants.EVENT_PAGE_CATEGORIES.MATCHES) {
          return SportsbookActions.fetchEventMainCategories({
            eventMainCategories: eventPageData.eventMainCategories,
            eventMarkesCategories: eventPageData.eventMarkesCategories,
            games: [],
            specialMarkets: null,
            leagueName: eventPageData.leagueName,
            event: eventPageData.event,
            parentId: eventPageData.parentId,
            selectedEventMainCategory,
            selectedMarketsCategory: eventPageData.selectedMarketsCategory,
          });
        }
        if (selectedEventMainCategory.name === SportsConstants.EVENT_PAGE_CATEGORIES.ADVANCE_TICKETS) {
          return SportsbookActions.fetchEventEarlylMarketGames({
            eventMainCategories: eventPageData.eventMainCategories,
            eventMarkesCategories: eventPageData.eventMarkesCategories,
            games: [],
            specialMarkets: null,
            leagueName: eventPageData.leagueName,
            event: eventPageData.event,
            parentId: eventPageData.parentId,
            selectedEventMainCategory,
            selectedMarketsCategory: eventPageData.selectedMarketsCategory,
          });
        }
        return SportsbookActions.fetchEventSpecialMarketGames({
          eventMainCategories: eventPageData.eventMainCategories,
          eventMarkesCategories: eventPageData.eventMarkesCategories,
          games: [],
          specialMarkets: null,
          leagueName: eventPageData.leagueName,
          event: eventPageData.event,
          parentId: eventPageData.parentId,
          selectedEventMainCategory,
          selectedMarketsCategory: eventPageData.selectedMarketsCategory,
        });
      }),
    ),
  );

  fetchEventSpecialMarketGames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.fetchEventSpecialMarketGames),
      switchMap((eventPageData: any) => {
        const params = {
          parentId: eventPageData.selectedEventMainCategory.nodeId,
          sportHandle: eventPageData.event.SportHandle,
        };
        this.sportTrackPollingService.stopAllPollings();
        return this.sportService.getEvents(params).pipe(
          map((specialMarketEvents) =>
            SportsbookActions.setEventGames({
              eventMainCategories: eventPageData.eventMainCategories,
              eventMarkesCategories: [],
              games: specialMarketEvents,
              specialMarkets: null,
              leagueName: eventPageData.leagueName,
              event: eventPageData.event,
              parentId: eventPageData.parentId,
              selectedEventMainCategory: eventPageData.selectedEventMainCategory,
              selectedMarketsCategory: eventPageData.selectedMarketsCategory,
            }),
          ),
          catchError((e) => {
            this.sportsbookFacade.setSportsError({
              errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_ERROR,
              errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_3,
            });
            return [];
          }),
        );
      }),
    ),
  );

  fetchEventEarlylMarketGames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.fetchEventEarlylMarketGames),
      switchMap((eventPageData: any) => {
        const params = {
          parentId: eventPageData.selectedEventMainCategory.nodeId,
          sportHandle: eventPageData.event.SportHandle,
        };
        this.sportTrackPollingService.stopAllPollings();
        return this.sportService.getEvents(params).pipe(
          map((earlyMarketEvents) =>
            SportsbookActions.setEventGames({
              eventMainCategories: eventPageData.eventMainCategories,
              eventMarkesCategories: [],
              games: [],
              specialMarkets: earlyMarketEvents.length > 0 ? earlyMarketEvents[0] : null,
              leagueName: eventPageData.leagueName,
              event: eventPageData.event,
              parentId: eventPageData.parentId,
              selectedEventMainCategory: eventPageData.selectedEventMainCategory,
              selectedMarketsCategory: eventPageData.selectedMarketsCategory,
            }),
          ),
          catchError((e) => {
            this.sportsbookFacade.setSportsError({
              errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_ERROR,
              errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_4,
            });
            return [];
          }),
        );
      }),
    ),
  );

  fetchEventGames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.fetchEventGames),
      switchMap((eventPageData: any) => {
        const params = {
          parentId: eventPageData.parentId,
          category: eventPageData.selectedMarketsCategory.CategoryId,
          sportHandle: eventPageData.event.SportHandle,
        };
        this.sportTrackPollingService.startEventPagePolling();
        return this.bufferFacade
          .newAuxBuffer(
            this.sportService,
            "getEventsCountryBuffer",
            params,
            this.timer.regular,
            this.sportTrackPollingService.eventPolling$,
          )
          .pipe(
            takeWhile((_) => this.sportTrackPollingService.eventPolling$.getValue()),
            map(([prev, current]) => {
              const gamesData = this.sportBookService.mapEvents(
                prev,
                current,
                eventPageData.selectedMarketsCategory.CategoryId,
                eventPageData.event,
              );
              return SportsbookActions.setEventGames({
                eventMainCategories: eventPageData.eventMainCategories,
                eventMarkesCategories: eventPageData.eventMarkesCategories,
                games: gamesData.leagueMarkets,
                specialMarkets: eventPageData.specialMarkets,
                leagueName: eventPageData.leagueName,
                event: { ...eventPageData.event, marketNames: gamesData.marketNames },
                parentId: eventPageData.parentId,
                selectedEventMainCategory: eventPageData.selectedEventMainCategory,
                selectedMarketsCategory: eventPageData.selectedMarketsCategory,
              });
            }),
            catchError((e) => {
              this.sportsbookFacade.setSportsError({
                errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_POLLING_ERROR,
                errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_5,
              });
              return [];
            }),
          );
      }),
    ),
  );
}
